import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { controlerSuppressionCompte, supprimerCompte, SuppressionCompte, useMoi } from '../Auth/moi.ts';

// Mot a recopier pour confirmer la suppression.
const CONFIRMATION = 'SUPPRIMER';

/**
 * Zone de danger (page Mes preferences) : suppression du compte. Un athlete
 * ne peut supprimer son compte que s'il n'est plus suivi par un coach ; un
 * coach, que s'il n'a plus d'athlete (regles verifiees par le serveur, cf
 * CompteService).
 */
export default function ZoneDangerEditeur() {
  const moi = useMoi();
  const [controle, setControle] = React.useState<SuppressionCompte | null>(null);
  const [ouvert, setOuvert] = React.useState(false);
  const [saisie, setSaisie] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);

  // Recontrole quand le coach ou les athletes changent (ex : athlete qui quitte son coach).
  React.useEffect(() => {
    controlerSuppressionCompte().then(setControle).catch((e) => setErreur((e as Error).message));
  }, [moi?.coach, moi?.athletes.length]);

  const fermer = () => {
    if (enCours) return;
    setOuvert(false);
    setSaisie('');
  };

  const supprimer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await supprimerCompte();
    } catch (e) {
      setErreur('Suppression impossible : ' + (e as Error).message);
      setEnCours(false);
      setOuvert(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <h2>Zone de danger</h2>
      <Box sx={{ border: 1, borderColor: 'error.main', borderRadius: 1, p: 2 }}>
        <Typography variant="h6" color="error" sx={{ mb: 1 }}>Supprimer mon compte</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {moi?.role === 'COACH'
            ? 'Tes préférences, tes séances personnelles, ton calendrier et tes activités sont effacés définitivement. '
              + 'Les séances versées à la bibliothèque du club restent au club.'
            : 'Tes préférences, ton calendrier, tes séances et tes activités sont effacés définitivement.'}
          {' '}Tu pourras te reconnecter avec ton compte Google : tu repartiras d'un compte vierge.
        </Typography>
        {controle && !controle.possible && (
          <Alert severity="warning" sx={{ mb: 2 }}>{controle.raison}</Alert>
        )}
        <Button
          color="error"
          variant="contained"
          disabled={!controle?.possible}
          onClick={() => setOuvert(true)}
        >
          Supprimer mon compte
        </Button>
      </Box>
      {erreur && <Alert severity="error" sx={{ mt: 2 }}>{erreur}</Alert>}

      <Dialog open={ouvert} onClose={fermer} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer ton compte ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Cette action est irréversible. Pour confirmer, saisis <strong>{CONFIRMATION}</strong>.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            disabled={enCours}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fermer} disabled={enCours}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            disabled={enCours || saisie.trim().toUpperCase() !== CONFIRMATION}
            onClick={supprimer}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
